import React from 'react';
import { UserLabelsSelection, WrapperEntitySelector } from '@components/Inputs';
import StatusPage from '../../Status/StatusInput';
import { NewPaperWithPadding } from '@components/Misc';
import { Grid, TextInput, Checkbox, Typography } from '@ts/storybook';
import { CEntity, CStatusIndicator, CTaskType, IEntity, IForm } from '@ts/ui-models';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';

interface IEventTypeTaskRulesTabProps extends IForm {}

const EventTypeTaskRulesTab = (props: IEventTypeTaskRulesTabProps) => {
  const { mode, values, onCallbackChange } = props;
  const isViewMode = mode === 'view';

  const handleChangeEntitySelector = (entityName: string) => (ids: string | string[] | null) => {
    onCallbackChange(entityName + 'Id')(ids);
  };

  const onChangeTaskType = (taskType: CTaskType | null) => {
    if (taskType != null) {
      onCallbackChange('creationTaskTypeId')(taskType.getID());
      onCallbackChange('creationTaskType')(taskType);

      const responsibleUser = taskType.responsibleUser;
      if (responsibleUser && !values.newTaskResponsibleUser) {
        const responsibleUserId = new CEntity(responsibleUser).getID();
        onCallbackChange('newTaskResponsibleUser')(responsibleUser);
        onCallbackChange('newTaskResponsibleUserId')(responsibleUserId);
      }

      const userGroups = taskType.userGroups;
      if (userGroups) {
        onCallbackChange('userGroups')(userGroups);
      }
    } else {
      onCallbackChange('creationTaskTypeId')(null);
      onCallbackChange('creationTaskType')(null);
    }
  };

  const statusForTask = values.statusForTask;
  const statusIndicators = statusForTask?.indicators ?? [];

  const handleStatusListChange = (newList: CStatusIndicator[]) => {
    if (statusForTask) {
      onCallbackChange('statusForTask.indicators')(newList);
    } else {
      onCallbackChange('statusForTask')({
        type: 'operation',
        indicators: newList,
      });
    }
  };

  return (
    <Grid container>
      <Grid size={{ xs: 12 }}>
        <NewPaperWithPadding>
          <Grid container direction="column" spacing={2}>
            <Typography variant="h6" text="Create new task" />

            <Checkbox
              key="chkbx"
              isReadOnly={isViewMode}
              label="Create a new task when an event of this type happens"
              checked={values.createsTasks}
              onChangeValue={onCallbackChange('createsTasks')}
            />

            {values.createsTasks && (
              <>
                <WrapperEntitySelector
                  label={'Task type'}
                  searchComponent={generateSearchEntitySelector('taskType')}
                  isReadOnly={isViewMode}
                  value={values.creationTaskType ?? values.creationTaskTypeId}
                  onChangeEntity={v => onChangeTaskType(v as CTaskType | null)}
                  multiple={false}
                />
                <WrapperEntitySelector
                  label={'Assignee'}
                  searchComponent={generateSearchEntitySelector('user')}
                  isReadOnly={isViewMode}
                  value={values.newTaskResponsibleUserId}
                  multiple={false}
                  onChange={handleChangeEntitySelector('newTaskResponsibleUser')}
                />

                <UserLabelsSelection
                  key="addressedlabels"
                  label="Addressed to following user groups"
                  value={values.userGroups}
                  onChange={handleChangeEntitySelector('userGroups')}
                  disabled={isViewMode}
                  multiple={true}
                />

                <TextInput
                  key="prfx"
                  disabled={isViewMode}
                  label="Prefix for the task name"
                  value={values.creationTaskPrefix}
                  onChangeValue={onCallbackChange('creationTaskPrefix')}
                />

                <StatusPage
                  key="stts"
                  mode={mode}
                  title={'Initial status for the task'}
                  statesList={statusIndicators}
                  onChangeList={handleStatusListChange}
                />
              </>
            )}
          </Grid>
        </NewPaperWithPadding>
      </Grid>
    </Grid>
  );
};

export default EventTypeTaskRulesTab;
